import { useMemo, useState } from "react"
import { Box, Flex, Grid, Skeleton, Text } from "@chakra-ui/react"
import type { Graph } from "@hydrologiq/schemas/1.0.0/installationPage"
import TimeSeriesGraph from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import {
  buildLinearTimeAxisTicksMs,
  calculateDynamicAxisInterval,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import { boundarySourceFromAssignments } from "@hydrologiq/shared/monitoring/builders/2.0.0/TimeRange"
import { useTimeRangeController } from "@hydrologiq/shared/monitoring/builders/2.0.0/useTimeRangeController"
import { useAutoRefresh } from "@hydrologiq/shared/monitoring/builders/2.0.0/useAutoRefresh"
import { TimeRangeControl } from "@hydrologiq/shared/monitoring/builders/2.0.0/TimeRangeControl"
import { mapRangeGranularity } from "@hydrologiq/shared/monitoring/builders/2.0.0/RangeGranularity"
import { Section, SectionRoot } from "@hydrologiq/shared/chakra/custom/Section"
import { useGetPref, useSetPref } from "@hydrologiq/shared/providers/helpers/usePref"
import Informative from "@hydrologiq/shared/Informative"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import {
  useAssetMonitoringGraphs,
  type MonitoringGraphPayload,
} from "../../hooks/useAssetMonitoringGraphs"
import type { GetAssetQuery } from "@hydrologiq/gql-experience/graphql"
import HybridParentMonitoring from "./HybridParentMonitoring"

type Asset = GetAssetQuery["asset"]

export interface AssetTimescaleMonitoringProps {
  asset: Asset
}

/** Loading skeleton for individual graph cards */
function ChartLoadingSkeleton() {
  return (
    <Box p={4}>
      <Skeleton height="20px" width="200px" mb={4} />
      <Skeleton height="250px" width="100%" />
    </Box>
  )
}

/**
 * Build a Graph descriptor from a server-resolved MonitoringGraphPayload.
 * The server provides title and unit; we map them to the schema Graph shape
 * required by TimeSeriesGraph.
 */
function buildGraphDescriptor(payload: MonitoringGraphPayload): Graph {
  const yAxis = payload.unit ? `${payload.title} (${payload.unit})` : payload.title
  const title = payload.title

  return {
    title,
    yAxis,
    size: "full" as const,
    type: "TimeSeries" as const,
    source: {
      field: payload.graphId,
      type: "TimescaleDB" as const,
    },
    versioning: { schema: "1.0.0", revision: 1 },
  }
}

/**
 * Server-driven asset monitoring component.
 *
 * Renders one chart per server-resolved graph. Multiple series sharing one axis
 * are rendered on a single combined chart (the server groups them).
 * No hardcoded class-specific logic — the server owns which graphs an asset shows.
 *
 * For hybrid parent assets, delegates to HybridParentMonitoring which renders
 * system-style aggregated graphs using shared monitoring primitives.
 */
function AssetTimescaleMonitoring({ asset }: AssetTimescaleMonitoringProps) {
  // Hybrid parent detection: hybrid parents get the system-style monitoring view
  const isHybridParent =
    asset?.isParent === true && asset?.class?.toLowerCase() === "hybrid"

  if (isHybridParent) {
    return <HybridParentMonitoring asset={asset} />
  }

  // Non-hybrid path continues below (existing server-driven approach)
  return <AssetTimescaleMonitoringInner asset={asset} />
}

/** Inner component for non-hybrid asset monitoring (existing path). */
function AssetTimescaleMonitoringInner({ asset }: AssetTimescaleMonitoringProps) {
  const getPref = useGetPref()
  const setPref = useSetPref()

  // Detect if asset is a parent (has children) for stacked graph rendering
  const isParent = asset?.isParent === true

  const browserTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const boundarySource = useMemo(
    () =>
      boundarySourceFromAssignments(
        asset.assignments?.range ?? asset.assignments?.all ?? [],
        asset.assignments?.monitored
      ),
    [asset]
  )

  const { applied, apply, refresh, refreshKey } = useTimeRangeController({
    boundarySource,
    browserTimeZone,
    persistedPreset: getPref("timescalePeriod"),
    onRelativePresetApplied: (preset) => setPref("timescalePeriod", preset),
  })

  const autoRefresh = useAutoRefresh({ apply, refresh, enabled: applied.mode === "relative" })

  const startDate = applied.startUtc
  const endDate = applied.endUtc

  const rangeTimescale = useMemo(
    () => mapRangeGranularity(startDate, endDate),
    [startDate, endDate]
  )

  const { graphs, loading } = useAssetMonitoringGraphs({
    assetId: asset.id,
    start: startDate.toIsoString(),
    stop: endDate.toIsoString(),
    granularity: rangeTimescale.granularity,
    refreshKey,
  })

  /** Overlay toggle state: graphId → Set of active overlay graphIds */
  const [graphOverlayState, setGraphOverlayState] = useState<Map<string, Set<string>>>(
    new Map()
  )

  /**
   * Initialize overlay defaults when graphs arrive from the server.
   * Only initializes once per set of graphs (avoids resetting user toggles on re-render).
   */
  const overlayDefaultsInitialized = useMemo(() => {
    if (!graphs.length) return false
    const needsInit = graphs.some(
      (g) => g.availableOverlays?.length && !graphOverlayState.has(g.graphId)
    )
    if (needsInit) {
      setGraphOverlayState((prev) => {
        const next = new Map(prev)
        for (const g of graphs) {
          if (g.availableOverlays?.length && !next.has(g.graphId)) {
            const defaults = g.availableOverlays
              .filter((o) => o.defaultEnabled)
              .map((o) => o.graphId)
            if (defaults.length) {
              next.set(g.graphId, new Set(defaults))
            }
          }
        }
        return next
      })
    }
    return true
  }, [graphs])
  // Suppress unused variable warning — the memo exists for its side effect
  void overlayDefaultsInitialized

  /** Compute shared timestamps across all graphs for consistent x-axis alignment */
  const sharedTimestamps = useMemo(() => {
    if (loading || graphs.length === 0) return undefined

    const timestampSet = new Set<string>()
    for (const graph of graphs) {
      for (const point of graph.data) {
        if (point.timestamp) {
          timestampSet.add(point.timestamp as string)
        }
      }
    }

    return Array.from(timestampSet).sort()
  }, [graphs, loading])

  const sharedXAxisInterval = useMemo(() => {
    if (!sharedTimestamps?.length) return undefined
    return calculateDynamicAxisInterval(startDate, endDate, sharedTimestamps.length)
  }, [startDate, endDate, sharedTimestamps])

  const sharedXAxisTicksMs = useMemo(
    () => buildLinearTimeAxisTicksMs(startDate, endDate, 8),
    [startDate, endDate]
  )

  /** Check if any graph has data */
  const hasAnyData = useMemo(() => {
    if (graphs.length === 0) return false
    return graphs.some((g) => g.data.length > 0)
  }, [graphs])

  // Show empty state when loading is complete but no data returned
  if (!loading && !hasAnyData && graphs.length > 0) {
    return (
      <SectionRoot>
        <Section separator={false}>
          <Flex direction="column" gap={0} p={0}>
            <Box mt={{ base: 2, md: 4 }}>
              <TimeRangeControl
                applied={applied}
                boundarySource={boundarySource}
                browserTimeZone={browserTimeZone}
                onApply={autoRefresh.apply}
                onOpenChange={autoRefresh.onOpenChange}
              />
            </Box>

            <Box mt={{ base: 6, md: 8 }}>
              <Informative
                heading="No monitoring data available"
                text="There is no TimescaleDB monitoring data for this asset in the selected time range."
              />
            </Box>
          </Flex>
        </Section>
      </SectionRoot>
    )
  }

  return (
    <SectionRoot>
      <Section separator={false}>
        <Flex direction="column" gap={0} p={0}>
          <Box mt={{ base: 2, md: 4 }}>
            <TimeRangeControl
              applied={applied}
              boundarySource={boundarySource}
              browserTimeZone={browserTimeZone}
              onApply={autoRefresh.apply}
              onOpenChange={autoRefresh.onOpenChange}
            />
          </Box>

          <Grid
            templateColumns={{ base: "1fr", lg: "1fr" }}
            gap={{ base: 6, md: 8 }}
            mt={{ base: 6, md: 8 }}
            width="100%"
            minW={0}
          >
            {graphs.map((graphPayload) => {
              const graphDescriptor = buildGraphDescriptor(graphPayload)

              // Compute overlay data for this graph
              const activeOverlays = graphOverlayState.get(graphPayload.graphId)
              const hasOverlays = Boolean(
                graphPayload.availableOverlays?.length && graphPayload.overlays?.length
              )

              let overlayDataProp: {
                data: typeof graphPayload.data
                series: typeof graphPayload.series
                yAxisLabel: string
                useSameYAxis?: boolean
              } | undefined = undefined

              if (hasOverlays && activeOverlays?.size) {
                // Find the first active overlay config + data
                const activeConfig = graphPayload.availableOverlays?.find(
                  (o) => activeOverlays.has(o.graphId)
                )
                const activeData = graphPayload.overlays?.find(
                  (o) => activeOverlays.has(o.graphId)
                )
                if (activeConfig && activeData && activeData.data.length > 0) {
                  overlayDataProp = {
                    data: activeData.data,
                    series: activeData.series,
                    yAxisLabel: activeConfig.yAxisLabel,
                    useSameYAxis: activeConfig.sameAxis,
                  }
                }
              }

              return (
                <Box
                  key={graphPayload.graphId}
                  gridColumn="1 / -1"
                  minW={0}
                  bg="card"
                  borderRadius="5px"
                  boxShadow="card"
                  p={4}
                  minHeight="100px"
                >
                  {loading ? (
                    <ChartLoadingSkeleton />
                  ) : graphPayload.data.length === 0 ? (
                    <Box p={4}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>
                        {graphPayload.title}
                      </Text>
                      <Text fontSize="sm" color="fg.muted">
                        No data available for this period.
                      </Text>
                    </Box>
                  ) : (
                    <>
                      {graphPayload.availableOverlays?.length ? (
                        <Flex alignItems="center" gap={3} flexWrap="wrap" mb={2} justifyContent="flex-end">
                          {graphPayload.availableOverlays.map((overlay) => (
                            <Flex key={overlay.graphId} alignItems="center" gap={2}>
                              <Text fontSize="sm" color="fg.muted">
                                {overlay.label}
                              </Text>
                              <Switch
                                checked={activeOverlays?.has(overlay.graphId) ?? overlay.defaultEnabled}
                                onCheckedChange={(details) => {
                                  setGraphOverlayState((prev) => {
                                    const next = new Map(prev)
                                    const current = next.get(graphPayload.graphId) || new Set<string>()
                                    if (details.checked) {
                                      current.add(overlay.graphId)
                                    } else {
                                      current.delete(overlay.graphId)
                                    }
                                    next.set(graphPayload.graphId, new Set(current))
                                    return next
                                  })
                                }}
                              />
                            </Flex>
                          ))}
                        </Flex>
                      ) : null}
                      <TimeSeriesGraph
                        graph={graphDescriptor}
                        timeseries={rangeTimescale.timeseries}
                        startDate={startDate}
                        endDate={endDate}
                        data={graphPayload.data}
                        series={graphPayload.series}
                        sharedTimestamps={sharedTimestamps}
                        xAxisTickInterval={sharedXAxisInterval}
                        xAxisTicksMs={sharedXAxisTicksMs}
                        height="sm"
                        hideAssetMenu
                        stacked={isParent}
                        overlayData={overlayDataProp}
                      />
                    </>
                  )}
                </Box>
              )
            })}
          </Grid>
        </Flex>
      </Section>
    </SectionRoot>
  )
}

export default AssetTimescaleMonitoring
